import React, { CSSProperties, useEffect, useState } from 'react';
import cn from 'classnames';
import styles from './Ticker.module.scss';
import { useIncrementingCount, useGlobalContactCount } from '@repo/hooks';
import { ClayGlobalRelationshipsData } from '@repo/utils/types';

const TickerDigit = ({ digit }: { digit: string }) => {
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [currentDigit, setCurrentDigit] = useState(digit);
  const [nextDigit, setNextDigit] = useState(digit);

  useEffect(() => {
    if (!isTransitioning && currentDigit !== digit) {
      setIsTransitioning(true);
      setNextDigit(digit);

      setTimeout(() => {
        setCurrentDigit(digit);
        setIsTransitioning(false);
      }, 500);
    }
  }, [digit, isTransitioning]);

  return (
    <div className={cn(styles.digit, isTransitioning && styles.transitioning)}>
      <div
        className={cn(styles.digitPane, styles.digitPaneTop, styles.next)}
        style={{ '--digit': `"${nextDigit}"` } as CSSProperties}
      />
      <div
        className={cn(styles.digitPane, styles.digitPaneBottom, styles.current)}
        style={{ '--digit': `"${currentDigit}"` } as CSSProperties}
      />
      <div
        className={cn(styles.digitPane, styles.digitPaneTop, styles.current)}
        style={{ '--digit': `"${currentDigit}"` } as CSSProperties}
      />
      <div
        className={cn(styles.digitPane, styles.digitPaneBottom, styles.next)}
        style={{ '--digit': `"${nextDigit}"` } as CSSProperties}
      />
    </div>
  );
};

const AnimatedCount = ({ relationships }: { relationships: ClayGlobalRelationshipsData }) => {
  const { contactCount, getCurrentContactCount } = useGlobalContactCount(relationships);
  const displayedCount = useIncrementingCount({
    startCount: contactCount,
    getCurrentCount: getCurrentContactCount,
  });
  const digits = String(displayedCount).split('');

  return (
    <div className={styles.relationshipCount}>
      {digits.map((digit, i) => (
        <TickerDigit key={i} digit={digit} />
      ))}
    </div>
  );
};

export const getCurrentRelationshipCount = async (): Promise<ClayGlobalRelationshipsData> => {
  const contactCount = await fetch('https://api.clay.earth/api/contact-count/').then((res) =>
    res.json(),
  );

  const startCount = contactCount.previousHigh.count;
  const currentHighCount = contactCount.currentHigh.count + 150000;
  const incrementStartTime = new Date(contactCount.previousHigh.date).getTime();

  return { startCount, currentHighCount, incrementStartTime };
};

export default function Ticker({ relationships }: { relationships: ClayGlobalRelationshipsData }) {
  return <div>{relationships && <AnimatedCount relationships={relationships} />}</div>;
}
